// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - PANNEAU PROPRIÉTÉS GRAFCET
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/grafcet/components/GrafcetPropertiesPanel.jsx
// Panneau d'édition des propriétés des blocs et transitions
// Papy 73+ - Joshua approved! 🚀
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import '../styles/GrafcetPropertiesPanel.css';

// ═══════════════════════════════════════════════════════════════════════════
// ÉDITEUR DE PROPRIÉTÉS NODE
// ═══════════════════════════════════════════════════════════════════════════

function NodePropertiesEditor({ node, onUpdate, capteurs }) {
  const [label, setLabel] = useState(node.data.label || '');
  const [config, setConfig] = useState(node.data.config || {});

  useEffect(() => {
    setLabel(node.data.label || '');
    setConfig(node.data.config || {});
  }, [node]);

  const handleLabelChange = (e) => {
    setLabel(e.target.value);
    onUpdate(node.id, { label: e.target.value });
  };

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onUpdate(node.id, { config: newConfig });
  };

  const renderConfigFields = () => {
    switch (node.data.type_etape) {
      case 'lecture_capteur':
        return (
          <div className="grafcet-props__section">
            <h4>📊 Configuration Capteur</h4>
            <div className="grafcet-form-group">
              <label>Capteur</label>
              <select
                value={config.capteur_id || ''}
                onChange={(e) => handleConfigChange('capteur_id', parseInt(e.target.value))}
              >
                <option value="">Sélectionner...</option>
                {capteurs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom} ({c.type_capteur})
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'condition':
        return (
          <div className="grafcet-props__section">
            <h4>❓ Configuration Condition</h4>
            <div className="grafcet-form-row">
              <div className="grafcet-form-group">
                <label>Opérateur</label>
                <select
                  value={config.operateur || 'lt'}
                  onChange={(e) => handleConfigChange('operateur', e.target.value)}
                >
                  <option value="lt">&lt; Inférieur</option>
                  <option value="gt">&gt; Supérieur</option>
                  <option value="eq">= Égal</option>
                  <option value="lte">≤ Inf. ou égal</option>
                  <option value="gte">≥ Sup. ou égal</option>
                  <option value="neq">≠ Différent</option>
                </select>
              </div>
              <div className="grafcet-form-group">
                <label>Seuil</label>
                <input
                  type="number"
                  value={config.seuil || ''}
                  onChange={(e) => handleConfigChange('seuil', parseFloat(e.target.value))}
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        );

      case 'action_simple':
        return (
          <div className="grafcet-props__section">
            <h4>⚡ Configuration Action</h4>
            <div className="grafcet-form-group">
              <label>Commande</label>
              <div className="grafcet-radio-group">
                <label className="grafcet-radio">
                  <input
                    type="radio"
                    name="commande"
                    value="on"
                    checked={config.commande === 'on'}
                    onChange={() => handleConfigChange('commande', 'on')}
                  />
                  <span className="grafcet-radio__label">🟢 ON</span>
                </label>
                <label className="grafcet-radio">
                  <input
                    type="radio"
                    name="commande"
                    value="off"
                    checked={config.commande === 'off'}
                    onChange={() => handleConfigChange('commande', 'off')}
                  />
                  <span className="grafcet-radio__label">🔴 OFF</span>
                </label>
              </div>
            </div>
            <div className="grafcet-form-group">
              <label>Durée (optionnel)</label>
              <div className="grafcet-input-with-unit">
                <input
                  type="number"
                  value={config.duree || ''}
                  onChange={(e) => handleConfigChange('duree', parseInt(e.target.value))}
                  placeholder="0 = permanent"
                />
                <span className="grafcet-unit">sec</span>
              </div>
            </div>
          </div>
        );

      case 'timer':
        return (
          <div className="grafcet-props__section">
            <h4>⏱️ Configuration Timer</h4>
            <div className="grafcet-form-group">
              <label>Durée d'attente</label>
              <div className="grafcet-input-with-unit">
                <input
                  type="number"
                  value={config.duree || 60}
                  onChange={(e) => handleConfigChange('duree', parseInt(e.target.value))}
                  min="1"
                />
                <span className="grafcet-unit">sec</span>
              </div>
              <div className="grafcet-form-hint">
                = {formatDuration(config.duree || 60)}
              </div>
            </div>
          </div>
        );

      case 'notification':
        return (
          <div className="grafcet-props__section">
            <h4>🔔 Configuration Notification</h4>
            <div className="grafcet-form-group">
              <label>Message</label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Message à envoyer..."
                rows={3}
              />
            </div>
            <div className="grafcet-form-group">
              <label>Type</label>
              <select
                value={config.type_notif || 'email'}
                onChange={(e) => handleConfigChange('type_notif', e.target.value)}
              >
                <option value="email">📧 Email</option>
                <option value="sms">📱 SMS</option>
                <option value="push">🔔 Push</option>
              </select>
            </div>
          </div>
        );

      case 'ia_conseil':
        return (
          <div className="grafcet-props__section">
            <h4>🤖 Configuration IA</h4>
            <div className="grafcet-form-group">
              <label>Type de conseil</label>
              <select
                value={config.type_conseil || 'optimisation'}
                onChange={(e) => handleConfigChange('type_conseil', e.target.value)}
              >
                <option value="optimisation">🎯 Optimisation</option>
                <option value="diagnostic">🔍 Diagnostic</option>
                <option value="prediction">🔮 Prédiction</option>
                <option value="alerte">⚠️ Analyse alerte</option>
              </select>
            </div>
          </div>
        );

      case 'attente_validation':
        return (
          <div className="grafcet-props__section">
            <h4>✋ Configuration Validation</h4>
            <div className="grafcet-form-group">
              <label>Timeout</label>
              <div className="grafcet-input-with-unit">
                <input
                  type="number"
                  value={config.timeout || 3600}
                  onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
                  min="60"
                />
                <span className="grafcet-unit">sec</span>
              </div>
              <div className="grafcet-form-hint">
                = {formatDuration(config.timeout || 3600)}
              </div>
            </div>
            <div className="grafcet-form-group">
              <label>Message validation</label>
              <input
                type="text"
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Confirmer l'action ?"
              />
            </div>
          </div>
        );

      case 'boucle':
        return (
          <div className="grafcet-props__section">
            <h4>🔄 Configuration Boucle</h4>
            <div className="grafcet-form-group">
              <label>Nombre d'itérations</label>
              <input
                type="number"
                value={config.iterations || 1}
                onChange={(e) => handleConfigChange('iterations', parseInt(e.target.value))}
                min="1"
                max="100"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="grafcet-props__node">
      <div className="grafcet-props__section">
        <h4>📝 Informations</h4>
        <div className="grafcet-form-group">
          <label>Nom de l'étape</label>
          <input
            type="text"
            value={label}
            onChange={handleLabelChange}
            placeholder="Nom..."
          />
        </div>
        <div className="grafcet-props__type">
          Type: <strong>{node.data.type_etape}</strong>
        </div>
      </div>
      {renderConfigFields()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ÉDITEUR DE PROPRIÉTÉS EDGE (TRANSITION)
// ═══════════════════════════════════════════════════════════════════════════

function EdgePropertiesEditor({ edge, onUpdate }) {
  const [label, setLabel] = useState(edge.label || '');
  const [typeCondition, setTypeCondition] = useState(edge.data?.type_condition || 'toujours');
  const [conditionConfig, setConditionConfig] = useState(edge.data?.condition_config || {});
  const [priorite, setPriorite] = useState(edge.data?.priorite || 1);

  useEffect(() => {
    setLabel(edge.label || '');
    setTypeCondition(edge.data?.type_condition || 'toujours');
    setConditionConfig(edge.data?.condition_config || {});
    setPriorite(edge.data?.priorite || 1);
  }, [edge]);

  const handleUpdate = (updates) => {
    onUpdate(edge.id, updates);
  };

  const handleTypeChange = (type) => {
    setTypeCondition(type);
    handleUpdate({ type_condition: type });
  };

  const handleConfigChange = (key, value) => {
    const newConfig = { ...conditionConfig, [key]: value };
    setConditionConfig(newConfig);
    handleUpdate({ condition_config: newConfig });
  };

  const renderConditionConfig = () => {
    switch (typeCondition) {
      case 'timeout':
        return (
          <div className="grafcet-form-group">
            <label>Durée timeout</label>
            <div className="grafcet-input-with-unit">
              <input
                type="number"
                value={conditionConfig.duree || 60}
                onChange={(e) => handleConfigChange('duree', parseInt(e.target.value))}
              />
              <span className="grafcet-unit">sec</span>
            </div>
          </div>
        );

      case 'capteur_seuil':
        return (
          <>
            <div className="grafcet-form-group">
              <label>Résultat condition</label>
              <div className="grafcet-radio-group">
                <label className="grafcet-radio">
                  <input
                    type="radio"
                    checked={conditionConfig.result === true}
                    onChange={() => handleConfigChange('result', true)}
                  />
                  <span className="grafcet-radio__label">✅ OUI (vrai)</span>
                </label>
                <label className="grafcet-radio">
                  <input
                    type="radio"
                    checked={conditionConfig.result === false}
                    onChange={() => handleConfigChange('result', false)}
                  />
                  <span className="grafcet-radio__label">❌ NON (faux)</span>
                </label>
              </div>
            </div>
          </>
        );

      case 'condition_custom':
        return (
          <div className="grafcet-form-group">
            <label>Expression</label>
            <input
              type="text"
              value={conditionConfig.expression || ''}
              onChange={(e) => handleConfigChange('expression', e.target.value)}
              placeholder="temp > 25 AND hygro < 60"
            />
            <div className="grafcet-form-hint">
              Variables: temp, hygro, ph, lum...
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="grafcet-props__edge">
      <div className="grafcet-props__section">
        <h4>🔗 Transition</h4>
        <div className="grafcet-form-group">
          <label>Label (affiché sur la flèche)</label>
          <input
            type="text"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              handleUpdate({ label: e.target.value });
            }}
            placeholder="OUI, NON, TIMEOUT..."
          />
        </div>
      </div>

      <div className="grafcet-props__section">
        <h4>⚡ Condition</h4>
        <div className="grafcet-form-group">
          <label>Type de condition</label>
          <select
            value={typeCondition}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="toujours">➡️ Toujours (automatique)</option>
            <option value="timeout">⏱️ Timeout</option>
            <option value="capteur_seuil">📊 Résultat condition</option>
            <option value="feedback_ok">✅ Feedback OK</option>
            <option value="feedback_error">❌ Feedback Erreur</option>
            <option value="condition_custom">🔧 Expression custom</option>
            <option value="ia_evaluation">🤖 Évaluation IA</option>
            <option value="meteo_seuil">🌤️ Seuil météo</option>
            <option value="date_atteinte">📅 Date atteinte</option>
            <option value="stade_culture">🌱 Stade culture</option>
          </select>
        </div>
        {renderConditionConfig()}
      </div>

      <div className="grafcet-props__section">
        <h4>⚙️ Options</h4>
        <div className="grafcet-form-group">
          <label>Priorité (si plusieurs transitions)</label>
          <input
            type="number"
            value={priorite}
            onChange={(e) => {
              setPriorite(parseInt(e.target.value));
              handleUpdate({ priorite: parseInt(e.target.value) });
            }}
            min="1"
            max="10"
          />
          <div className="grafcet-form-hint">
            1 = plus haute priorité
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds} secondes`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}min`;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export default function GrafcetPropertiesPanel({
  selectedNode,
  selectedEdge,
  onUpdateNode,
  onUpdateEdge,
  onClose,
  capteurs
}) {
  return (
    <div className="grafcet-props">
      <div className="grafcet-props__header">
        <h3 className="grafcet-props__title">
          {selectedNode ? '📦 Propriétés Étape' : '🔗 Propriétés Transition'}
        </h3>
        <button className="grafcet-props__close" onClick={onClose}>
          ×
        </button>
      </div>
      
      <div className="grafcet-props__content">
        {selectedNode && (
          <NodePropertiesEditor
            node={selectedNode}
            onUpdate={onUpdateNode}
            capteurs={capteurs}
          />
        )}
        {selectedEdge && (
          <EdgePropertiesEditor
            edge={selectedEdge}
            onUpdate={onUpdateEdge}
          />
        )}
      </div>
    </div>
  );
}
